import { FieldErrors } from "./interface/loginUser";
import { ProjectDetails } from "./interface/project";
import { UserFieldErrors } from "./interface/userListing";


const ADD_DESCRIPTION = 'Add user details and profile image.';

/** Basic RFC-5321-style format check; Cognito handles actual email verification. */
const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface AuthError {
    name?: string;
    message?: string;
}

/** Validate URL before fetch or href: only https and hostname ending with .amazonaws.com. */
const isSafeUrl = (url: string): boolean => {
    try {
        const parsed = new URL(url);
        return parsed.protocol === "https:" && parsed.hostname.endsWith(".amazonaws.com");
    } catch {
        return false;
    }
};

/**
 * Validate URL for use in <img src>. Rejects javascript:, data:, and other non-https schemes.
 * Allows https: URLs and same-origin relative paths (starting with /).
 */
export const isSafeImageUrl = (url: string | null | undefined): boolean => {
    const s = (url ?? "").trim();
    if (!s) return false;
    const lower = s.toLowerCase();
    // Avoid literal "javascript:" to satisfy eslint no-script-url while still blocking the scheme.
    const jsScheme = ["java", "script:"].join("");
    if (lower.startsWith(jsScheme) || lower.startsWith("data:")) return false;
    try {
        const parsed = new URL(s);
        return parsed.protocol === "https:";
    } catch {
        return s.startsWith("/");
    }
};

/** Returns url if it is safe for <img src>, otherwise fallback. Use for API-sourced profile image URLs. */
export const getSafeImageSrc = (
    url: string | null | undefined,
    fallback: string
): string => {
    const s = (url ?? "").trim();
    return isSafeImageUrl(s) ? s : fallback;
};

/**
 * Parse AWS S3 presigned URL expiry time (ms since epoch).
 * Supports SigV4 query params: X-Amz-Date (YYYYMMDDTHHMMSSZ) and X-Amz-Expires (seconds).
 * Returns null when URL is not a parseable presigned URL.
 */
const getPresignedUrlExpiryMs = (url: string): number | null => {
    try {
        const parsed = new URL(url);
        const amzDate = parsed.searchParams.get("X-Amz-Date");
        const amzExpires = parsed.searchParams.get("X-Amz-Expires");
        if (!amzDate || !amzExpires) return null;
        const expiresSeconds = Number(amzExpires);
        if (!Number.isFinite(expiresSeconds) || expiresSeconds <= 0) return null;

        // Format: 20260319T045520Z
        const m = amzDate.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
        if (!m) return null;
        const [, y, mo, d, h, mi, s] = m;
        const issuedMs = Date.UTC(
            Number(y),
            Number(mo) - 1,
            Number(d),
            Number(h),
            Number(mi),
            Number(s)
        );
        if (!Number.isFinite(issuedMs)) return null;
        return issuedMs + expiresSeconds * 1000;
    } catch {
        return null;
    }
};

/**
 * Returns true when the presigned URL is expired (or about to expire).
 * Adds a small skew buffer so users don't hit a 403 mid-request.
 */
const isPresignedUrlExpired = (url: string, skewMs: number = 30_000): boolean => {
    const expiryMs = getPresignedUrlExpiryMs(url);
    if (expiryMs === null) return false;
    return Date.now() + skewMs >= expiryMs;
};

const isEmail = (v: string): boolean => EMAIL_FORMAT.test(v);

const validate = (email: string, password: string): FieldErrors => {
    const errs: FieldErrors = {};
    if (!email) errs.email = "Email is required.";
    else if (!isEmail(email)) errs.email = "Please enter a valid email address.";
    if (!password) errs.password = "Password is required.";
    return errs;
}

const validateUserForm = (formData: {
    firstName: string;
    lastName: string;
    email: string;
    company?: string;
    jobTitle?: string;
    phone?: string;
}): UserFieldErrors => {
    const errs: UserFieldErrors = {};

    // First Name validation (optional): only letters and spaces; no numbers or special characters
    if (formData.firstName.trim() !== "") {
        if (formData.firstName.trim().length < 2) {
            errs.firstName = "Full name must be at least 2 characters.";
        } else if (formData.firstName.trim().length > 50) {
            errs.firstName = "Full name must not exceed 50 characters.";
        } else if (!/^[a-zA-Z\s]+$/.test(formData.firstName.trim())) {
            errs.firstName = "Full name cannot contain numbers or special characters. Only letters and spaces are allowed.";
        }
    }

    // Email validation (only mandatory field)
    if (!formData.email.trim()) {
        errs.email = "Email is required.";
    } else if (!isEmail(formData.email)) {
        errs.email = "Please enter a valid email address.";
    }

    // Phone validation: only numeric digits, min 3 and max 15 digits when provided
    if (formData.phone !== undefined && formData.phone.trim() !== "") {
        const phoneDigits = formData.phone.trim();
        if (!/^\d+$/.test(phoneDigits)) {
            errs.phone = "Phone number must contain only digits.";
        } else if (phoneDigits.length < 3) {
            errs.phone = "Phone number must have at least 3 digits.";
        } else if (phoneDigits.length > 15) {
            errs.phone = "Phone number must not exceed 15 digits.";
        }
    }

    return errs;
}

const mapAuthError = (err: unknown): string => {
    const error = err as AuthError;

    const name = error?.name || "";
    const msg = error?.message || "";

    switch (name) {
        case "UserNotConfirmedException":
            return "Your account isn't confirmed. Check your email for the verification link.";
        case "NotAuthorizedException":
            return "Incorrect email or password.";
        case "UserNotFoundException":
            return "No account found for that email.";
        case "UsernameExistsException":
            return "An account with this email already exists.";
        case "PasswordResetRequiredException":
            return "You need to reset your password before signing in.";
        case "TooManyFailedAttemptsException":
        case "LimitExceededException":
            return "Too many attempts. Please try again later.";
        default:
            return msg || "Something went wrong. Please try again.";
    }
};

/** Safe user-facing messages for known backend error codes. Never expose data.message/data.error to UI (F-025). */
const SAFE_ERRORS: Record<string, string> = {
    DUPLICATE_EMAIL: "An account with this email already exists.",
    DUPLICATE_USER: "An account with this email already exists.",
    EMAIL_ALREADY_EXISTS: "An account with this email already exists.",
    NOT_FOUND: "Resource not found.",
    UNAUTHORIZED: "Unauthorized. Please log in again.",
    FORBIDDEN: "You don't have permission to perform this action.",
    VALIDATION_ERROR: "Validation error. Please check your input.",
    CONFLICT: "A conflict occurred. This resource may already exist.",
    RESOURCE_EXISTS: "A conflict occurred. This resource may already exist.",
};

const GENERIC_MESSAGE = "Something went wrong. Please try again.";

interface ErrorWithResponse {
    response?: { status?: number; data?: { code?: string; message?: string } };
    request?: unknown;
}

const extractApiError = (error: unknown): string => {
    if (!error) {
        return GENERIC_MESSAGE;
    }
    const err = error as ErrorWithResponse & { cause?: unknown };
    // Unwrap ApiError so callers get the same message for raw errors and ApiError
    if (typeof err === "object" && "cause" in err && err.cause !== undefined) {
        return extractApiError(err.cause);
    }

    // Handle axios errors: map known codes to safe messages; never expose data.message/data.error (F-025)
    if (err.response) {
        const status = err.response.status;
        const data = err.response.data;
        const code = data?.code && typeof data.code === "string" ? data.code : null;

        if (code && SAFE_ERRORS[code]) {
            return SAFE_ERRORS[code];
        }

        // Use API message when present for conflict (e.g. "User account already exists")
        const apiMessage = typeof data?.message === "string" ? data.message.trim() : "";
        if (status === 409 && apiMessage) {
            return apiMessage;
        }

        // Status-based safe messages only
        switch (status) {
            case 400:
                return "Invalid request. Please check your input.";
            case 401:
                return "Unauthorized. Please log in again.";
            case 403:
                return "You don't have permission to perform this action.";
            case 404:
                return "Resource not found.";
            case 409:
                return "A conflict occurred. This resource may already exist.";
            case 422:
                return "Validation error. Please check your input.";
            case 500:
                return "Server error. Please try again later.";
            default:
                return GENERIC_MESSAGE;
        }
    }

    // Handle network errors
    if (err.request) {
        return "Network error. Please check your connection and try again.";
    }

    return GENERIC_MESSAGE;
}

/** Min/max character limits for project form fields (used in validation and inputs). */
export const PROJECT_FIELD_LIMITS = {
    name: { min: 3, max: 100 },
    summary: { min: 0, max: 2000 },
    companyName: { min: 0, max: 100 },
    location: { min: 0, max: 100 },
} as const;

type ProjectFieldErrors = {
    name?: string;
    description?: string;
    summary?: string;
    companyName?: string;
    projectThumbnail?: string;
    status?: string;
    files?: string;
    userId?: string;
    location?: string;
    uploadControlPanelDoc?: string;
};

const validateProjectForm = (
    formData: ProjectDetails,
): ProjectFieldErrors => {
    const errs: ProjectFieldErrors = {};
    const { name, summary, companyName, location } = PROJECT_FIELD_LIMITS;

    // Project Name
    const nameVal = formData.name.trim();
    if (!nameVal) {
        errs.name = "Project name is required.";
    } else if (nameVal.length < name.min) {
        errs.name = `Project name must be at least ${name.min} characters.`;
    } else if (nameVal.length > name.max) {
        errs.name = `Project name must be at most ${name.max} characters.`;
    }

    // Summary
    const summaryVal = formData.summary.trim();
    if (summaryVal.length > summary.max) {
        errs.summary = `Summary must be at most ${summary.max} characters.`;
    }

    // Company Name
    const companyVal = formData.companyName.trim();
    if (companyVal.length > companyName.max) {
        errs.companyName = `Company name must be at most ${companyName.max} characters.`;
    }

    // Location
    const locationVal = formData.location.trim();
    if (locationVal.length > location.max) {
        errs.location = `Location must be at most ${location.max} characters.`;
    }

    // Status
    if (!formData.status) {
        errs.status = "Project status is required.";
    }
    return errs;
};




/** Allowed profile picture file extensions (recommended). */
const ALLOWED_PROFILE_IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp"];

/**
 * Returns an error message if the file's extension is not allowed for profile images, otherwise null.
 * Allowed: .jpg, .jpeg, .png, .webp
 */
const getProfileImageExtensionError = (file: File): string | null => {
    const name = file.name.toLowerCase();
    const hasAllowed = ALLOWED_PROFILE_IMAGE_EXTENSIONS.some((ext) => name.endsWith(ext));
    if (!hasAllowed) {
        return "Profile picture must be .jpg, .jpeg, .png, or .webp.";
    }
    return null;
};

/**
 * Get download filename from fetch Response (Content-Disposition) or URL path.
 * Preserves original file name and extension when server sends Content-Disposition.
 */
export function getFileNameFromDownloadResponse(
    response: Response,
    downloadUrl: string,
    fallback: string
): string {
    const disposition = response.headers.get("Content-Disposition");
    if (disposition) {
        const utf8Match = disposition.match(/filename\*?=UTF-8''(.+?)(?:;|$)/i);
        if (utf8Match && utf8Match[1]) {
            try {
                return decodeURIComponent(utf8Match[1].replace(/^["']|["']$/g, "").trim());
            } catch { /* ignore */ }
        }
        const match = disposition.match(/filename=["']?([^"'\s;]+)["']?/i);
        if (match && match[1]) {
            try {
                return decodeURIComponent(match[1].trim());
            } catch {
                return match[1].trim();
            }
        }
    }
    try {
        const path = new URL(downloadUrl).pathname;
        const segment = path.split("/").filter(Boolean).pop();
        if (segment) return decodeURIComponent(segment);
    } catch { /* ignore */ }
    return fallback;
}

export { isEmail, isSafeUrl, getPresignedUrlExpiryMs, isPresignedUrlExpired, validate, validateUserForm, mapAuthError, validateProjectForm, extractApiError, ADD_DESCRIPTION, getProfileImageExtensionError, ALLOWED_PROFILE_IMAGE_EXTENSIONS };